import Api from "@/api"
import { ContentLoading } from "@/components/loading/StateLoading"
import { IncidentStatus, SystemRole, type IncidentLog } from "@/models"
import BasicPage from "@/pages/BasicPage"
import NotFound from "@/pages/NotFound"
import { createCoreDataRefreshRef } from "@/stores/coreDataStore"
import { watchImmediate } from "@vueuse/core"
import { computed, reactive } from "vue"
import { useRouter } from "vue-router"
import { defineComponent, loadLoadable, useLoadableRef, type ReactiveComponent } from "vue-utils"
import AuthorizeRole from "../AuthorizeRole"
import EditExistingIncident from "./EditExistingIncident"
import ViewSignedOffIncident from "./ViewSignedOffIncident"

const EditIncidentPage: ReactiveComponent = () => {
	const router = useRouter()
	const coreDataRefresh = createCoreDataRefreshRef()

	const incidentLogId = computed(() => {
		const route = router.currentRoute.value
		if (!("incidentLogId" in route.params)) {
			return null
		}
		const id = Number.parseInt(String(route.params.incidentLogId))
		if (Number.isSafeInteger(id) && id > 0) {
			return id
		}
		return null
	})

	const allDone = computed(() => [incidentLogRef, coreDataRefresh].every((s) => s.type === "done"))

	const incidentLogRef = useLoadableRef(async () => {
		const id = incidentLogId.value
		if (id === null) {
			//If no incident Id is specified or is not a valid number, fallback to nothing found
			return null
		}
		const log = await Api.incidents.getById(id)
		return log ? (reactive(log) as IncidentLog) : null
	})

	watchImmediate(incidentLogId, () => void loadLoadable(incidentLogRef))

	function IncidentLogWrapper() {
		if (incidentLogRef.type !== "done" || !incidentLogRef.result) {
			return null
		}

		const incident = incidentLogRef.result
		if (incident.status === IncidentStatus.SignedOff) {
			return <ViewSignedOffIncident incident={incident} />
		}

		return <EditExistingIncident incidentId={incident.id} refresh={() => void loadLoadable(incidentLogRef)} />
	}

	function getTitle() {
		if (incidentLogRef.type !== "done" || !incidentLogRef.result) {
			return "View Incident"
		}
		const status = incidentLogRef.result.status
		if (status === IncidentStatus.Open) {
			return "Edit Incident"
		}
		return "View Incident"
	}

	return () => {
		if (incidentLogRef.type === "done" && !incidentLogRef.result) {
			return <NotFound />
		}
		return (
			<AuthorizeRole role={SystemRole.CoastguardReadOnly}>
				<BasicPage title={getTitle()} plain={allDone.value} scroll={!allDone.value}>
					<ContentLoading stores={[incidentLogRef, coreDataRefresh]}>
						<IncidentLogWrapper />
					</ContentLoading>
				</BasicPage>
			</AuthorizeRole>
		)
	}
}

export default defineComponent(EditIncidentPage)
